import React from 'react';
import { Box, Grid, Typography, useTheme, useMediaQuery } from '@mui/material';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isAdminOrManager } from '@/shared/utils/roles';

// Dashboard widgets
import TodaysTasksWidget from '../components/TodaysTasksWidget';
import LiveTasksWidget from '../components/LiveTasksWidget';
import DailyPerformanceWidget from '../components/DailyPerformanceWidget';
import BandwidthWidget from '../components/BandwidthWidget';
import UpcomingDeadlinesWidget from '../components/UpcomingDeadlinesWidget';
import ActiveTimerWidget from '../components/ActiveTimerWidget';
import QuickActionsWidget from '../components/QuickActionsWidget';
import LeaveRequestsWidget from '../components/LeaveRequestsWidget';
import LeaveBalanceWidget from '../components/LeaveBalanceWidget';

// Manager-specific widgets
import TeamWorkloadWidget from '../components/TeamWorkloadWidget';
import PendingApprovalsWidget from '../components/PendingApprovalsWidget';

/**
 * DashboardPage
 * Role-based dashboard showing relevant widgets
 * Individual contributors see their tasks, managers see team overview
 * Mobile-first responsive design
 */
const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useAppSelector(selectCurrentUser);

  const isManager = isAdminOrManager(user?.role);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Box>
      {/* Header - Compact on mobile */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {getGreeting()}, {user?.firstName || 'there'}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: 0.5, display: { xs: 'none', sm: 'block' } }}
        >
          Here's what's happening with your tasks today.
        </Typography>
      </Box>

      {/* Mobile Layout - Single column, prioritized widgets */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Active Timer - Most important on mobile */}
          <ActiveTimerWidget />
          
          {/* Quick Actions - Easy access */}
          <QuickActionsWidget />
          
          {/* Today's Tasks - Tasks you've picked for today */}
          <TodaysTasksWidget />
          
          {/* Live Tasks - Active execution window */}
          <LiveTasksWidget />
          
          {/* Performance Metrics */}
          <Box sx={{ display: 'flex', gap: 2, '& > *': { flex: 1 } }}>
            <DailyPerformanceWidget />
          </Box>
          
          {/* Bandwidth */}
          <BandwidthWidget />
          
          {/* Upcoming Deadlines */}
          <UpcomingDeadlinesWidget />
          
          {/* Manager: Team Workload */}
          {isManager && <TeamWorkloadWidget />}
          
          {/* Manager: Pending Approvals */}
          {isManager && <PendingApprovalsWidget />}
          
          {/* Leave Requests */}
          <LeaveRequestsWidget />

          {/* Leave Balance */}
          <LeaveBalanceWidget />
        </Box>
      ) : (
        /* Desktop/Tablet Layout - Grid */
        <Grid container spacing={3}>
          {/* Left Column - Primary content */}
          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              {/* Today's Tasks - Tasks you've picked for today */}
              <Grid item xs={12}>
                <TodaysTasksWidget />
              </Grid>

              {/* Live Tasks - Active execution window */}
              <Grid item xs={12}>
                <LiveTasksWidget />
              </Grid>

              {/* Daily Performance - Hit/Miss */}
              <Grid item xs={12} md={6}>
                <DailyPerformanceWidget />
              </Grid>

              {/* Bandwidth */}
              <Grid item xs={12} md={6}>
                <BandwidthWidget />
              </Grid>

              {/* Manager: Team Workload */}
              {isManager && (
                <Grid item xs={12}>
                  <TeamWorkloadWidget />
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Right Column - Secondary content */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={3}>
              {/* Active Timer */}
              <Grid item xs={12}>
                <ActiveTimerWidget />
              </Grid>

              {/* Quick Actions */}
              <Grid item xs={12}>
                <QuickActionsWidget />
              </Grid>

              {/* Upcoming Deadlines */}
              <Grid item xs={12}>
                <UpcomingDeadlinesWidget />
              </Grid>

              {/* Leave Requests */}
              <Grid item xs={12}>
                <LeaveRequestsWidget />
              </Grid>

              {/* Leave Balance */}
              <Grid item xs={12}>
                <LeaveBalanceWidget />
              </Grid>

              {/* Manager: Pending Approvals */}
              {isManager && (
                <Grid item xs={12}>
                  <PendingApprovalsWidget />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardPage;
